/**
 * Менеджер превью боя
 * Вынесено из legacy-app.js для модульности
 * 
 * Управляет отображением превью существ перед боем, инициализацией
 * элементов управления и загрузкой экрана боя.
 */

/**
 * @typedef {Object} PreviewState
 * @property {Object|null} player - Превью существа игрока
 * @property {Object|null} enemy - Превью существа противника
 */

/**
 * @typedef {Object} BattlePreviewManager
 * @property {Function} updateBattlePreview - Обновляет превью боя
 * @property {Function} initializeBattleSelects - Инициализирует селекты существ
 * @property {Function} loadBattleScreen - Загружает экран боя
 */

/**
 * Создает менеджер превью боя
 * 
 * @param {Object} params - Параметры менеджера
 * @param {Function} params.ensureCollectionData - Функция обеспечения данных коллекции (Bs)
 * @param {Function} params.formatCreaturePreview - Функция форматирования превью существа (Cs)
 * @param {Function} params.formatAiPresetPreview - Функция форматирования превью AI пресета (Ms)
 * @param {Function} params.formatRandomCreaturePreview - Функция форматирования случайного существа (Hs)
 * @param {Function} params.getCreaturePreview - Функция получения превью существа (on)
 * @param {Function} params.renderCreaturePreview - Функция рендеринга превью существа (an)
 * @param {Function} params.parseUserCreatureId - Функция парсинга ID существа (Ne)
 * @param {Function} params.findCreatureInCollection - Функция поиска существа в коллекции (Ps)
 * @param {Function} params.findAiPreset - Функция поиска AI пресета (Lt)
 * @param {Function} params.updatePvpUI - Функция обновления UI PvP (ze)
 * @param {Function} params.escapeHtml - Функция экранирования HTML (k)
 * @param {Function} params.initializeBattleHandlers - Функция инициализации обработчиков боя (Ke)
 * @param {Function} params.setupBattleModalHandlers - Функция настройки обработчиков модального окна (ps)
 * @param {Function} params.setupBattlePlaybackControls - Функция настройки элементов управления (us)
 * @param {Function} params.loadBattleHistory - Функция загрузки истории боев (Ye)
 * @param {Function} params.checkErrorbornStatus - Функция проверки статуса Errorborn (Ee)
 * @param {Function} params.startPvpSearch - Функция запуска поиска PvP
 * @param {Function} params.cancelPvpSearch - Функция отмены поиска PvP
 * @param {Array} params.collection - Коллекция существ (Y)
 * @param {Array} params.aiTemplates - Шаблоны AI (bn)
 * @param {Object} params.currentUser - Текущий пользователь (d)
 * @param {PreviewState} params.previewState - Состояние превью (J, X)
 * @param {Function} params.setPreviewState - Функция установки состояния превью
 * @returns {BattlePreviewManager} Объект с методами менеджера превью
 */
export function createBattlePreviewManager({
  ensureCollectionData,
  formatCreaturePreview,
  formatAiPresetPreview,
  formatRandomCreaturePreview,
  getCreaturePreview,
  renderCreaturePreview,
  parseUserCreatureId,
  findCreatureInCollection,
  findAiPreset,
  updatePvpUI,
  escapeHtml,
  initializeBattleHandlers,
  setupBattleModalHandlers,
  setupBattlePlaybackControls,
  loadBattleHistory,
  checkErrorbornStatus,
  startPvpSearch,
  cancelPvpSearch,
  collection,
  aiTemplates,
  currentUser,
  previewState,
  setPreviewState,
}) {
  /**
   * Обновляет превью боя на основе выбранных существ
   * 
   * Получает выбранные существа из селектов и обновляет
   * превью панели для игрока и противника.
   * 
   * @returns {void}
   */
  function updateBattlePreview() {
    const playerSelect = document.getElementById("battle-my-creature");
    const enemySelect = document.getElementById("battle-enemy-creature");
    
    const playerPreview = getCreaturePreview(playerSelect ? playerSelect.value : "", "player");
    const enemyPreview = getCreaturePreview(enemySelect ? enemySelect.value : "", "enemy");
    
    // Обновляем состояние превью
    setPreviewState({ player: playerPreview, enemy: enemyPreview });
    
    // Показываем только существо игрока
    renderCreaturePreview("battle-preview-player", playerPreview, "player");
    
    // Скрываем панель противника
    const enemyPreviewEl = document.getElementById("battle-preview-enemy");
    if (enemyPreviewEl) {
      enemyPreviewEl.style.display = 'none';
    }
    
    // Обновляем UI PvP
    updatePvpUI();
  }

  /**
   * Инициализирует селекты существ для боя
   */
  function initializeBattleSelects() {
    const playerSelect = document.getElementById("battle-my-creature");
    const enemySelect = document.getElementById("battle-enemy-creature");
    
    if (!playerSelect || !enemySelect) return;
    
    // Заполняем селект существ игрока
    if (collection.length) {
      playerSelect.innerHTML = collection.map((creature) => {
        const name = `${escapeHtml(creature.name || "Без имени")} • ур. ${creature.level || 1}`;
        return `<option value="user:${creature.creatureId}:${creature.id}">${name}</option>`;
      }).join("");
    } else {
      playerSelect.innerHTML = '<option value="">Нет доступных существ</option>';
    }
    
    // Заполняем селект противников
    const enemyOptions = aiTemplates.map((template) => 
      `<option value="${template.id}">${escapeHtml(template.label)}</option>`
    );
    
    if (collection.length) {
      enemyOptions.unshift('<option value="user:random">Случайный из коллекции</option>');
    }
    
    enemySelect.innerHTML = enemyOptions.join("");
    
    // Устанавливаем обработчики изменений
    playerSelect.onchange = updateBattlePreview;
    enemySelect.onchange = updateBattlePreview;
    
    // Обновляем превью
    updateBattlePreview();
  }

  /**
   * Загружает экран боя
   */
  async function loadBattleScreen() {
    // Обеспечиваем наличие данных коллекции
    await ensureCollectionData();
    
    // Инициализируем селекты
    initializeBattleSelects();
    
    // Инициализируем обработчики боя
    if (typeof initializeBattleHandlers === "function") {
      initializeBattleHandlers();
    }
    
    // Инициализация обработчиков модального окна
    if (typeof setupBattleModalHandlers === "function") {
      const modal = document.getElementById("battle-modal");
      if (modal && !modal.dataset.handlersBound) {
        setupBattleModalHandlers();
        modal.dataset.handlersBound = "1";
      }
    }
    
    // Инициализация элементов управления
    if (typeof setupBattlePlaybackControls === "function") {
      const playButton = document.getElementById("battle-playback-play");
      if (playButton && !playButton.dataset.controlsBound) {
        setupBattlePlaybackControls();
        playButton.dataset.controlsBound = "1";
      }
    }
    
    // Привязываем обработчики PvP кнопок
    const pvpSearchBtn = document.getElementById("battle-pvp-search-btn");
    if (pvpSearchBtn && !pvpSearchBtn.dataset.bound) {
      pvpSearchBtn.dataset.bound = "1";
      pvpSearchBtn.addEventListener("click", () => {
        startPvpSearch().catch((error) => console.error("startPvpSearch failed:", error));
      });
    }
    
    const pvpCancelBtn = document.getElementById("battle-pvp-cancel-btn");
    if (pvpCancelBtn && !pvpCancelBtn.dataset.bound) {
      pvpCancelBtn.dataset.bound = "1";
      pvpCancelBtn.addEventListener("click", () => {
        cancelPvpSearch().catch((error) => console.error("cancelPvpSearch failed:", error));
      });
    }
    
    // Обновляем UI PvP
    updatePvpUI();
    
    // Загружаем историю боев и проверяем статус Errorborn
    await loadBattleHistory();
    await checkErrorbornStatus(true);
  }

  return {
    updateBattlePreview,
    initializeBattleSelects,
    loadBattleScreen,
  };
}

